import { twMerge } from "tailwind-merge";
import { Button, Card, H2 } from "@sellar-io/components";
import { useLocation, useNavigate } from "react-router";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { DISCOVER, LANDING, SUPPLIERS } from "../../constants/routes";
import { Alert } from "../../components/alert/Alert";
import { ReactNode, useState } from "react";
import { AlertType } from "../../components/alert/AlertType";
import { EducateConnectSuppliers } from "./EducateConnectSuppliers";
import { useSupplierListEducate } from "../../hooks/api/v2/supplier/useSupplierListEducate";
import { EducateConnectPotentialSuppliers } from "./EducateConnectPotentialSuppliers";
import { EducateConnectMarket } from "./EducateConnectMarket";
import { useMenuDrawerContext } from "../../components/menu/MenuDrawerContextProvider.shared";

type EducateConnectSteps =
  | "ConnectedSuppliers"
  | "PotentialSuppliers"
  | "DiscoverSuppliers";

type EducateConnectStepType = {
  name: EducateConnectSteps;
  title: ReactNode;
  summary: ReactNode;
  continueStep?: EducateConnectSteps;
  leaveRoute?: string;
  secondaryRoute?: string;
  cta?: string;
  secondaryCta?: string;
  alertType?: AlertType;
  alertMessage?: string;
};

const educateConnectSteps: {
  ConnectedSuppliers: EducateConnectStepType;
  PotentialSuppliers: EducateConnectStepType;
  DiscoverSuppliers: EducateConnectStepType;
} = {
  ConnectedSuppliers: {
    name: "ConnectedSuppliers",
    title: "Trade ordering made simple with all of your existing suppliers",
    summary: (
      <>
        When you order on Sellar, you're ordering <strong>direct</strong> with
        suppliers. This means your weekly ordering will benefit from faster
        response times, using accurate live data, with no middle-man fees. All
        in one place.
      </>
    ),
    continueStep: "PotentialSuppliers",
    alertType: "success",
    alertMessage:
      "We've found some suppliers who already have you set up as a customer on Sellar!",
  },
  PotentialSuppliers: {
    name: "PotentialSuppliers",
    title: "Do you already have an account with these suppliers?",
    summary: (
      <>
        You can order from suppliers you already work with all in one place. Set
        up your account with them here on Sellar for convenient ordering with
        your usual pricing and payment terms.
      </>
    ),
    continueStep: "DiscoverSuppliers",
    alertType: "information",
    alertMessage:
      "We've found some suppliers you might already be working with",
  },
  DiscoverSuppliers: {
    name: "DiscoverSuppliers",
    title: (
      <>
        There are even more great suppliers selling <u>direct</u> on Sellar
      </>
    ),
    summary: (
      <>
        Discover new suppliers on the Sellar Market to order packaged and
        draught products from across the U.K.,{" "}
        <strong>even if you don't yet have an account with them</strong>. Check
        out their pricing and availability by visiting their storefronts.
      </>
    ),
    leaveRoute: DISCOVER,
    secondaryRoute: SUPPLIERS,
    cta: "Continue Browsing",
    secondaryCta: "Go to my suppliers",
  },
};

export const EducateConnect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderSupplierId: number | undefined = location.state?.orderSupplierId;
  const skipConnected: boolean | undefined = location.state?.skipConnected;
  const [step, setStep] = useState<EducateConnectStepType>(
    educateConnectSteps.ConnectedSuppliers,
  );
  const { displayType, setDisplayType } = useMenuDrawerContext();

  if (displayType === "sticky") setDisplayType("rail");

  const supplierQuery = useSupplierListEducate({
    query: orderSupplierId
      ? [
          {
            type: "ignoreSuppliers" as const,
            modifier: "IN" as const,
            value: [orderSupplierId],
          },
        ]
      : [],
  });

  const isLoading = supplierQuery.isLoading;
  const supplierLength = supplierQuery.data?.pages[0]?.length ?? 0;

  const shouldSkipConnectedSuppliers =
    (!isLoading && !supplierLength) || skipConnected;
  if (
    shouldSkipConnectedSuppliers &&
    step === educateConnectSteps.ConnectedSuppliers
  ) {
    setStep(educateConnectSteps.PotentialSuppliers);
  }

  const progressStep = () => {
    if (step.continueStep)
      return setStep(educateConnectSteps[step.continueStep]);
    if (step.leaveRoute) {
      setDisplayType("sticky");
      navigate(step.leaveRoute);
    }
  };

  const renderStep = () => {
    switch (step.name) {
      case educateConnectSteps.ConnectedSuppliers.name:
        return <EducateConnectSuppliers supplierQuery={supplierQuery} />;
      case educateConnectSteps.PotentialSuppliers.name:
        return <EducateConnectPotentialSuppliers />;
      case educateConnectSteps.DiscoverSuppliers.name:
        return <EducateConnectMarket />;
    }
  };

  return (
    <>
      <NavigationBar
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => {
          navigate(LANDING);
        }}
        title={"Your suppliers"}
        borderBottom={false}
      />
      <div
        className={
          "m-auto flex w-full max-w-[1040px] flex-col items-start gap-4 px-2 pt-6 lg:h-full lg:px-4"
        }
      >
        <Card
          className={twMerge(
            "bg-white",
            "flex w-full flex-col overflow-auto border border-zinc-300 p-4 sm:p-8",
          )}
        >
          <div className="flex flex-col gap-2 text-center">
            <H2 className="text-lg sm:text-2xl">{step.title}</H2>
            <p>{step.summary}</p>
          </div>
        </Card>
        {!!step.alertType && !!step.alertMessage && (
          <Alert
            type={step.alertType}
            title={step.alertMessage}
            message=""
            className="w-full"
          />
        )}

        {renderStep()}

        <div className="flex w-full items-center justify-center gap-4">
          {!!step.secondaryRoute && (
            <Button
              color="secondary"
              className="w-[240px]"
              onClick={() => {
                if (step.secondaryRoute) {
                  setDisplayType("sticky");
                  navigate(step.secondaryRoute);
                }
              }}
            >
              {step.secondaryCta}
            </Button>
          )}
          <Button className="w-[240px]" onClick={progressStep}>
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </>
  );
};
